/**
 * AITransferRewriter — Panel de revisión del batch AI que reescribe plantillas
 * transfer para usar {{start_location}} y {{end_location}}.
 *
 * Flujo:
 *  1. Lanzar dry-run (procesa piloto: 4 patterns, ~76 plantillas).
 *  2. Ver drafts con diff Original ↔ Nueva propuesta.
 *  3. Aprobar (aplica al `description` con backup) o Rechazar por plantilla.
 *  4. Aprobar todo en lote (opcional).
 *
 * Solo Admin / Director-Operaciones / Director-Ventas pueden guardar cambios.
 */
import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Bot, Wand2, CheckCircle2, XCircle, RotateCcw, Loader2, Search,
  ChevronLeft, ChevronRight, MapPin, RefreshCw, AlertCircle, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

/* ─────────────────────── Helpers ─────────────────────── */

const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const StatusBadge = ({ status, applied }) => {
  const cfg = {
    draft:    { label: applied ? "Aplicado" : "Draft listo", cls: applied ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-blue-100 text-blue-800 border-blue-300" },
    skipped:  { label: "Skip",       cls: "bg-slate-100 text-slate-700 border-slate-300" },
    rejected: { label: "Rechazado",  cls: "bg-rose-100 text-rose-800 border-rose-300" },
    error:    { label: "Error",      cls: "bg-amber-100 text-amber-800 border-amber-300" },
    applied:  { label: "Aplicado",   cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  }[status] || { label: status, cls: "bg-slate-100 text-slate-700 border-slate-300" };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
};

// Resalta los placeholders {{start_location}} / {{end_location}} en verde-oliva.
const highlightPlaceholders = (html) => (html || "").replace(
  /\{\{(start_location|end_location)\}\}/g,
  '<mark style="background:#8a977133;color:#4a5040;padding:0 4px;border-radius:3px;font-weight:600;">{{$1}}</mark>',
);

/* ─────────────────────── Filas de la tabla ─────────────────────── */

const RewriteRow = ({ item, onApply, onReject, onRevert, expanded, onToggle, mutatingId }) => {
  const draft = item.ai_transfer_rewrite || {};
  const applied = !!draft.applied;
  const isExpanded = expanded === item.id;
  const isMutating = mutatingId === item.id;
  return (
    <div className="border-b" data-testid={`rw-row-${item.id}`}>
      <div className="grid grid-cols-1 items-center gap-2 px-3 py-2 hover:bg-muted/20 sm:grid-cols-[1fr_140px_120px_140px_180px]">
        <div className="min-w-0">
          <button onClick={() => onToggle(item.id)} className="group flex w-full items-center gap-2 text-left" data-testid={`rw-toggle-${item.id}`}>
            <ChevronDown className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
            <span className="truncate text-sm font-medium">{item.name}</span>
          </button>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 pl-6 text-[11px] text-muted-foreground">
            {(item.cities || []).slice(0, 3).map((c) => (
              <span key={c} className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{c}</span>
            ))}
            {item.transfer_pattern && <Badge variant="outline" className="text-[10px]">{item.transfer_pattern}</Badge>}
          </div>
        </div>
        <div className="pl-6 sm:pl-0"><StatusBadge status={draft.status} applied={applied} /></div>
        <div className="pl-6 text-[11px] text-muted-foreground sm:pl-0">
          {draft.new_html ? (
            <span>
              {(draft.new_html.match(/\{\{start_location\}\}/g) || []).length}× start<br />
              {(draft.new_html.match(/\{\{end_location\}\}/g) || []).length}× end
            </span>
          ) : "—"}
        </div>
        <div className="pl-6 text-[11px] text-muted-foreground sm:pl-0" title={draft.reason || ""}>
          {draft.reason ? draft.reason.slice(0, 60) + (draft.reason.length > 60 ? "…" : "") : "—"}
        </div>
        <div className="flex items-center gap-1 pl-6 sm:pl-0">
          {draft.status === "draft" && !applied && (
            <>
              <Button size="sm" variant="outline" onClick={() => onReject(item.id)} disabled={isMutating} data-testid={`rw-reject-${item.id}`}>
                <XCircle className="mr-1 h-3.5 w-3.5" />Rechazar
              </Button>
              <Button size="sm" onClick={() => onApply(item.id)} disabled={isMutating} data-testid={`rw-apply-${item.id}`}>
                {isMutating ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}Aplicar
              </Button>
            </>
          )}
          {applied && (
            <Button size="sm" variant="outline" onClick={() => onRevert(item.id)} disabled={isMutating} data-testid={`rw-revert-${item.id}`}>
              {isMutating ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1 h-3.5 w-3.5" />}Revertir
            </Button>
          )}
          {draft.status === "rejected" && <span className="text-[11px] italic text-muted-foreground">Descartado</span>}
          {draft.status === "skipped" && <span className="text-[11px] italic text-muted-foreground">Sin cambios</span>}
          {draft.status === "error" && <AlertCircle className="h-4 w-4 text-amber-600" title={draft.reason} />}
        </div>
      </div>

      {isExpanded && (
        <div className="grid grid-cols-1 gap-3 border-t bg-muted/10 p-4 sm:grid-cols-2" data-testid={`rw-diff-${item.id}`}>
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-rose-600">Original</p>
            <div className="max-h-64 overflow-auto rounded-md border bg-white p-3 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: item.description || "<em class='text-muted-foreground'>(vacío)</em>" }} />
          </div>
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Draft AI</p>
            <div className="max-h-64 overflow-auto rounded-md border-2 border-emerald-500/30 bg-white p-3 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: highlightPlaceholders(draft.new_html) || "<em class='text-muted-foreground'>(sin cambios)</em>" }} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────── Página principal ─────────────────────── */

const PATTERNS = [
  "airport_to_hotel", "hotel_to_airport",
  "train_station_to_hotel", "hotel_to_train_station",
];

export default function AITransferRewriter() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("draft");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [mutatingId, setMutatingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(null); // null | 'apply' | 'revert'

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["td-transfer-rewrite-preview", statusFilter],
    queryFn: async () => (await api.get(`/plantillas/transfer-rewrite/preview?status_filter=${statusFilter}&limit=500`)).data,
    refetchInterval: statusFilter === "draft" ? 15000 : false,   // polling mientras se genera
  });

  const dryRunMutation = useMutation({
    mutationFn: async () => (await api.post(`/plantillas/transfer-rewrite/dry-run`, {}, { timeout: 900_000 })).data,
    onSuccess: (r) => {
      toast.success(`AI Rewriter completado: ${r.rewritten} draft · ${r.skipped} skip · ${r.errors} err (${r.total} total)`);
      qc.invalidateQueries({ queryKey: ["td-transfer-rewrite-preview"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo lanzar el batch"),
  });

  const applyOne = async (id) => {
    setMutatingId(id);
    try {
      await api.post(`/plantillas/${id}/transfer-rewrite/apply`);
      toast.success("Draft aplicado (backup guardado)");
      await refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aplicar"); }
    setMutatingId(null);
  };
  const rejectOne = async (id) => {
    setMutatingId(id);
    try {
      await api.post(`/plantillas/${id}/transfer-rewrite/reject`);
      toast.success("Draft rechazado");
      await refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); }
    setMutatingId(null);
  };
  const revertOne = async (id) => {
    setMutatingId(id);
    try {
      await api.post(`/plantillas/${id}/transfer-rewrite/revert`);
      toast.success("Plantilla revertida al original");
      await refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo revertir"); }
    setMutatingId(null);
  };

  const filtered = useMemo(() => {
    const list = data?.items || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((x) =>
      (x.name || "").toLowerCase().includes(q) ||
      (x.cities || []).some((c) => c.toLowerCase().includes(q)) ||
      (x.transfer_pattern || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const stats = useMemo(() => {
    const items = data?.items || [];
    return {
      total: items.length,
      applied: items.filter((x) => x.ai_transfer_rewrite?.applied).length,
      pending: items.filter((x) => x.ai_transfer_rewrite?.status === "draft" && !x.ai_transfer_rewrite?.applied).length,
    };
  }, [data]);

  const applyAllPending = async () => {
    setBulkBusy("apply");
    try {
      const r = (await api.post(`/plantillas/transfer-rewrite/apply-all`, {}, { timeout: 300_000 })).data;
      if (r.failed > 0) {
        toast.warning(`Aplicadas ${r.applied} plantillas · ${r.failed} errores`);
      } else {
        toast.success(`Aplicadas ${r.applied} plantillas correctamente`);
      }
      await refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar en lote");
    } finally {
      setBulkBusy(null);
    }
  };

  const revertAllApplied = async () => {
    setBulkBusy("revert");
    try {
      const r = (await api.post(`/plantillas/transfer-rewrite/revert-all`, {}, { timeout: 300_000 })).data;
      if (r.failed > 0) {
        toast.warning(`Revertidas ${r.reverted} plantillas · ${r.failed} errores`);
      } else {
        toast.success(`Revertidas ${r.reverted} plantillas al texto original`);
      }
      await refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir en lote");
    } finally {
      setBulkBusy(null);
    }
  };

  return (
    <div className="space-y-4 p-6" data-testid="ai-transfer-rewriter">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold" data-testid="rw-title">
            <Bot className="h-6 w-6 text-primary" /> AI Rewrite — Plantillas Transfer
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Reescribe descripciones para usar <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{start_location}}"}</code> y <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{end_location}}"}</code>. Piloto sobre 4 patterns: airport↔hotel y train↔hotel. Cada draft se aprueba manualmente antes de aplicar.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="rw-refresh-btn">
            <RefreshCw className="mr-1.5 h-4 w-4" />Refrescar
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" disabled={dryRunMutation.isPending} data-testid="rw-dryrun-btn">
                {dryRunMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1.5 h-4 w-4" />}
                Lanzar AI Rewrite
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="rw-dryrun-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Lanzar el AI rewriter?</AlertDialogTitle>
                <AlertDialogDescription>
                  Procesará todas las plantillas transfer del piloto ({PATTERNS.join(", ")}) que no tengan draft aún. Puede tardar varios minutos. Los drafts NO se aplican automáticamente — los revisas y apruebas después.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => dryRunMutation.mutate()} data-testid="rw-dryrun-confirm">Lanzar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Stats + filtros */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Total en vista:</span>
            <strong className="tabular-nums" data-testid="rw-count-total">{stats.total}</strong>
            <span className="text-muted-foreground">·</span>
            <span className="text-blue-700">Pendientes: <strong className="tabular-nums" data-testid="rw-count-pending">{stats.pending}</strong></span>
            <span className="text-muted-foreground">·</span>
            <span className="text-emerald-700">Aplicados: <strong className="tabular-nums" data-testid="rw-count-applied">{stats.applied}</strong></span>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="rw-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft/Aplicado</SelectItem>
                <SelectItem value="skipped">Skip</SelectItem>
                <SelectItem value="rejected">Rechazados</SelectItem>
                <SelectItem value="error">Errores</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Buscar por nombre o ciudad…" className="h-8 w-56 pl-7 text-xs" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="rw-search" />
            </div>
            {stats.pending > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="default" size="sm" disabled={bulkBusy !== null} data-testid="rw-apply-all-btn">
                    {bulkBusy === "apply"
                      ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
                    Aplicar todos los pendientes ({stats.pending})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Aplicar {stats.pending} drafts?</AlertDialogTitle>
                    <AlertDialogDescription>Se sobrescribirá la descripción de cada plantilla y se guardará backup automático. Podrás revertir individualmente o en bloque después.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={applyAllPending} data-testid="rw-apply-all-confirm">Aplicar todos</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {stats.applied > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={bulkBusy !== null} data-testid="rw-revert-all-btn"
                    title="Restaura el texto original de todas las plantillas aplicadas (red de seguridad si los agentes reportan problemas).">
                    {bulkBusy === "revert"
                      ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
                    Revertir todos los aplicados ({stats.applied})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Revertir {stats.applied} plantillas al texto original?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Restaurará la descripción anterior (guardada en <code className="rounded bg-muted px-1 py-0.5 text-xs">description_pre_ai</code>) de todas las plantillas aplicadas. Los drafts AI se conservan y podrás volver a aplicarlos individualmente cuando quieras.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={revertAllApplied} data-testid="rw-revert-all-confirm">
                      Revertir todo
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm">
            <span>Plantillas · piloto (airport↔hotel · train↔hotel)</span>
            {dryRunMutation.isPending && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generando drafts…
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="hidden border-b bg-muted/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:grid sm:grid-cols-[1fr_140px_120px_140px_180px]">
            <div>Plantilla</div><div>Estado</div><div>Placeholders</div><div>Razón AI</div><div className="text-right">Acciones</div>
          </div>
          {isLoading ? (
            <div className="flex h-40 items-center justify-center" data-testid="rw-loading"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="rw-empty">
              {statusFilter === "draft"
                ? "Aún no hay drafts. Pulsa «Lanzar AI Rewrite» para generar."
                : "Sin resultados para este filtro."}
            </div>
          ) : (
            filtered.map((item) => (
              <RewriteRow key={item.id} item={item}
                expanded={expanded} onToggle={(id) => setExpanded(expanded === id ? null : id)}
                onApply={applyOne} onReject={rejectOne} onRevert={revertOne}
                mutatingId={mutatingId} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
